/**
 * The ONE "look at this" treatment, shared by every surface that points at an element.
 *
 * ## Why this is a shared token and not a per-feature style
 *
 * Two treatments that both mean "look at this" teach the user that they mean DIFFERENT
 * things. The product tour dims the page and rings the current step's anchor in brand
 * purple; VRG-549's "What does this change?" originally rang its elements in a dashed
 * indigo of its own invention. A reader who had seen both had no way to know the dashes
 * were not carrying some extra meaning — because in every other part of this product a
 * different treatment does carry one (§32's destructive tiers, §34's two card-hover
 * patterns). Spencer, on seeing it: *"just want to use the same tour spotlight — not the
 * dotted or hashed line spotlights."*
 *
 * So the ring geometry and its colours live here, in `@repo/ui`, and both consumers read
 * them: `apps/platform`'s `ProductTour` and the Studio token highlight, which runs inside
 * the `apps/app` preview iframe. That last one is why this cannot simply be a component —
 * the highlight is an injected stylesheet inside another app's shadow root, so what
 * crosses the boundary is CSS text, not JSX. `spotlightRingCss()` renders the same
 * geometry as a declaration block for that case; `SPOTLIGHT_*` are the raw values for the
 * React case.
 *
 * ## Why the colours come from `palette`, and why the consumer must NOT use its own theme
 *
 * They are read from `palette.brand` rather than typed here, so there is one hex.
 *
 * The subtle half: in `apps/platform`, `theme.palette.brand[600]` IS this value, so the
 * tour could keep reading its theme and nobody would notice. In `apps/app` it is NOT —
 * `settings/mui/palette.ts` replaces the whole `brand` ramp with one derived from the
 * tenant's own `primary_color`. A highlight drawn from the app's theme would therefore be
 * painted in the very colour the operator is about to change, and would vanish on exactly
 * the brands whose purple is close to Vera's. The mark must be theme-INDEPENDENT, which is
 * what importing a static value from here guarantees and what reading `useTheme()` would
 * quietly break.
 */

import { palette } from "./palette";

/*
  `palette` is a `PaletteOptions`, whose index signature does not carry the `brand` ramp's
  shape (it is added by module augmentation on the runtime `Palette`, not on the options
  object). Narrowed here once rather than at each read.
*/
const brand = (palette as unknown as { brand: Record<string | number, string> }).brand;

/** The ring itself. `palette.brand[600]` — Vera purple, never a tenant's. */
export const SPOTLIGHT_RING_COLOR = brand[600] as string;
/** The soft halo outside the ring. `palette.brand['600-32%']`. */
export const SPOTLIGHT_GLOW_COLOR = brand["600-32%"] as string;
/** px. The hairline. */
export const SPOTLIGHT_RING_WIDTH = 1;
/** px. The halo's spread beyond the ring. */
export const SPOTLIGHT_GLOW_WIDTH = 4;
/**
 * px. Matches the tour overlay's `borderRadius: 2` on the 4px spacing base.
 *
 * Only meaningful where the ring is drawn as its own box (the tour's overlay). An
 * `outline` follows the element's own corners, so `spotlightRingCss` does not set it.
 */
export const SPOTLIGHT_CORNER_RADIUS = 8;

/**
 * px. The floor a highlighted element's corners are raised to, so a ring around a
 * square-cornered element is not a hard rectangle.
 *
 * ## Why this is a floor applied per element, and not a property of the ring
 *
 * There is no way to round a ring independently of the thing it rings. `outline` and
 * `box-shadow` both follow the element's own `border-radius`, and `outline-radius` does not
 * exist (Firefox's `-moz-outline-radius` was removed). So "give the spotlight a slight
 * corner radius" can only be done by giving the ELEMENT one — which means the highlight is
 * now modifying the very surface the operator is judging, and that has to be bounded:
 *
 * - **It may only ever RAISE a radius, never lower one.** A flat `border-radius: 4px` would
 *   square off every pill (`999px`) and every rounded card it touched — a far larger lie
 *   than the square ring it set out to fix. The consumer reads the computed radius first and
 *   applies this only where all four corners are already `0px`.
 * - **It is suppressed entirely while `border_radius` is the token under review.** Rounding
 *   corners to annotate the control that sets corner radius would make the annotation
 *   disagree with the thing it is annotating.
 *
 * 4 rather than `SPOTLIGHT_CORNER_RADIUS`'s 8: the tour draws its ring as its own box around
 * a single anchor, where 8 reads as deliberate. Here the radius lands on the element itself,
 * so it has to be small enough to be a softening rather than a restyle.
 *
 * NOTE this is deliberately a SEPARATE constant from `SPOTLIGHT_CORNER_RADIUS` rather than a
 * change to it. The tour reads that one for its overlay geometry, and the two answer
 * different questions — changing 8 to soften a preview ring would silently retune the tour.
 */
export const SPOTLIGHT_PREVIEW_MIN_RADIUS = 4;

/**
 * The ring as a CSS declaration block, for stylesheets injected into a document this
 * code does not render — the Studio preview's shadow root and the email preview's
 * iframe.
 *
 * @param offset px between the element's border box and the ring. The tour uses 8
 * (`SPOTLIGHT_PAD`) around a single anchor. A multi-element highlight cannot: at 8px the
 * rings of adjacent rows in a list merge into one blob and stop saying "these things",
 * which is the whole point of the mark. 2 is the measured floor at which a ring still
 * reads as belonging to its element rather than being drawn on it.
 *
 * @param contrastHairline adds a 1px white ring INSIDE the halo. The tour needs no such
 * thing because it dims the rest of the page, so its ring always sits on a darkened
 * ground. This highlight deliberately does not dim — the operator is judging colours, and
 * a 55% black scrim over the preview would distort every one of them — so on a dark
 * `bg_color` the ring has to carry its own contrast.
 *
 * `outline`, not `border`: a border participates in layout, so switching one on would
 * shift the very page being judged. An outline paints outside the box and moves nothing.
 */
export const spotlightRingCss = ({
  offset = 2,
  contrastHairline = false,
  important = false,
}: {
  offset?: number;
  contrastHairline?: boolean;
  important?: boolean;
} = {}): string => {
  const bang = important ? " !important" : "";
  const halo = `0 0 0 ${SPOTLIGHT_GLOW_WIDTH}px ${SPOTLIGHT_GLOW_COLOR}`;
  const shadow = contrastHairline ? `0 0 0 1px #FFFFFF, ${halo}` : halo;
  return [
    `outline: ${SPOTLIGHT_RING_WIDTH}px solid ${SPOTLIGHT_RING_COLOR}${bang};`,
    `outline-offset: ${offset}px${bang};`,
    `box-shadow: ${shadow}${bang};`,
  ].join("\n  ");
};
